import { createContext, useContext } from 'react';

/**
 * Available theme names matching CSS [data-theme] attributes
 */
export type ThemeName =
  | 'neobrutal'
  | 'skeuomorphic'
  | 'flat'
  | 'minimalist'
  | 'glassmorphism'
  | 'claymorphism'
  | 'videogame'
  | 'cyberpunk';

/**
 * Theme metadata for UI display
 */
export interface ThemeMetadata {
  name: ThemeName;
  displayName: string;
  description: string;
  icon: string;
}

/**
 * Available themes with metadata
 */
export const THEMES: ThemeMetadata[] = [
  {
    name: 'neobrutal',
    displayName: 'Neobrutal',
    description: 'Bold, high contrast with thick borders and heavy shadows',
    icon: '⚡',
  },
  {
    name: 'skeuomorphic',
    displayName: 'Skeuomorphic',
    description: 'Realistic, textured with gradients and depth',
    icon: '🎨',
  },
  {
    name: 'flat',
    displayName: 'Flat',
    description: 'Clean and simple with no shadows',
    icon: '📐',
  },
  {
    name: 'minimalist',
    displayName: 'Minimalist',
    description: 'Subtle, clean with lots of whitespace',
    icon: '✨',
  },
  {
    name: 'glassmorphism',
    displayName: 'Glassmorphism',
    description: 'Transparent, frosted glass with backdrop blur',
    icon: '💎',
  },
  {
    name: 'claymorphism',
    displayName: 'Claymorphism',
    description: 'Soft, playful, 3D clay-like elements with vibrant colors',
    icon: '🪨',
  },
  {
    name: 'videogame',
    displayName: 'VideoGame',
    description: '90s arcade style with pixel art, bright colors and thick borders',
    icon: '🎮',
  },
  {
    name: 'cyberpunk',
    displayName: 'Cyberpunk',
    description: 'Dark Matrix/hacker aesthetic with neon green glow effects',
    icon: '💚',
  },
];

/**
 * Theme context value
 */
export interface ThemeContextValue {
  /** Current active theme */
  theme: ThemeName;

  /** Change the active theme */
  setTheme: (theme: ThemeName) => void;

  /** Available themes with metadata */
  themes: ThemeMetadata[];

  /** Get metadata for current theme */
  currentThemeMetadata: ThemeMetadata;
}

/**
 * Theme context
 */
export const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

/**
 * Hook to access theme context
 * @throws Error if used outside ThemeProvider
 */
export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
};
